import { useState, useEffect } from 'react'
import { Users, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useHouseholdStore } from '@/stores/household-store'
import { useAuthStore } from '@/stores/auth-store'
import { householdApi } from '@/services/household-api'
import { HouseholdCreateCard } from './household-create-card'
import { HouseholdCard } from './household-card'

export function HouseholdSettings() {
  const { user } = useAuthStore()
  const {
    households,
    currentHousehold,
    members,
    invites,
    loading,
    error,
    loadHouseholds,
    createHousehold,
    updateHousehold,
    deleteHousehold,
    loadMembers,
    loadInvites,
    createInvite,
    deleteInvite,
    removeMember,
    leaveHousehold,
    setCurrentHousehold,
    setError,
    clearError
  } = useHouseholdStore()

  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newHouseholdName, setNewHouseholdName] = useState('')
  const [editingHousehold, setEditingHousehold] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member')
  const [copiedInviteId, setCopiedInviteId] = useState<string | null>(null)
  const [inviteSubmitting, setInviteSubmitting] = useState(false)

  useEffect(() => {
    void loadHouseholds()
  }, [loadHouseholds])

  useEffect(() => {
    if (!currentHousehold?.id) {
      return
    }

    void loadMembers(currentHousehold.id)
    void loadInvites(currentHousehold.id)
  }, [currentHousehold?.id, loadInvites, loadMembers])

  const handleCreateHousehold = async () => {
    if (!newHouseholdName.trim()) return

    const household = await createHousehold(newHouseholdName.trim())
    if (household) {
      setNewHouseholdName('')
      setShowCreateForm(false)
    }
  }

  const handleUpdateHousehold = async (id: string) => {
    if (!editName.trim()) return

    const household = await updateHousehold(id, editName.trim())
    if (household) {
      setEditingHousehold(null)
      setEditName('')
    }
  }

  const handleDeleteHousehold = async (id: string, name: string) => {
    if (confirm(`Weet je zeker dat je het huishouden "${name}" wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`)) {
      await deleteHousehold(id)
    }
  }

  const handleCreateInvite = async () => {
    if (!inviteEmail.trim() || !currentHousehold) return

    const normalizedEmail = inviteEmail.trim().toLowerCase()

    if (normalizedEmail === user?.email?.trim().toLowerCase()) {
      setError('Je kunt jezelf niet uitnodigen voor dit huishouden.')
      return
    }

    if (invites.some((invite) => invite.email.trim().toLowerCase() === normalizedEmail)) {
      setError('Voor dit e-mailadres staat al een openstaande uitnodiging klaar.')
      return
    }

    setInviteSubmitting(true)

    try {
      const existingUser = await householdApi.findUserByEmail(normalizedEmail)

      if (!existingUser) {
        setError('Dit e-mailadres heeft nog geen account op KookInspiratie. Laat deze persoon eerst registreren.')
        return
      }

      if (members.some((member) => member.user_id === existingUser.user_id)) {
        setError('Deze gebruiker zit al in dit huishouden.')
        return
      }

      const invite = await createInvite(currentHousehold.id, normalizedEmail, inviteRole)

      if (invite) {
        setInviteEmail('')
        setShowInviteForm(false)
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Uitnodiging controleren mislukt.')
    } finally {
      setInviteSubmitting(false)
    }
  }

  const handleCopyInviteLink = (inviteCode: string) => {
    const inviteUrl = `${window.location.origin}/uitnodiging/${inviteCode}`
    navigator.clipboard.writeText(inviteUrl)
    setCopiedInviteId(inviteCode)
    setTimeout(() => setCopiedInviteId(null), 2000)
  }

  const getUserRole = (householdId: string) => {
    if (!user) return null
    
    // Use the members state instead of household.members
    const member = members.find(m => m.user_id === user.id && m.household_id === householdId)
    return member?.role || null
  }

  const isAdmin = (householdId: string) => {
    return getUserRole(householdId) === 'admin'
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center justify-between">
            <p className="text-red-600 text-sm">{error}</p>
            <Button variant="ghost" size="sm" onClick={clearError}>
              ×
            </Button>
          </div>
        </div>
      )}

      <HouseholdCreateCard
        showCreateForm={showCreateForm}
        newHouseholdName={newHouseholdName}
        loading={loading}
        onNameChange={setNewHouseholdName}
        onShowCreateForm={setShowCreateForm}
        onCreate={handleCreateHousehold}
      />

      {/* Existing Households */}
      {households.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Mijn Huishoudens
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {households.map((household) => (
              <HouseholdCard
                key={household.id}
                household={household}
                currentHouseholdId={currentHousehold?.id}
                isEditing={editingHousehold === household.id}
                editName={editName}
                loading={loading}
                isAdmin={isAdmin(household.id)}
                currentUserRole={getUserRole(household.id)}
                memberCount={household.member_count || 0}
                members={currentHousehold?.id === household.id ? members : []}
                invites={currentHousehold?.id === household.id ? invites : []}
                currentUserId={user?.id}
                currentUserDisplayName={user?.display_name || user?.name || user?.email || 'Jij'}
                showInviteForm={showInviteForm}
                inviteEmail={inviteEmail}
                inviteRole={inviteRole}
                inviteSubmitting={inviteSubmitting}
                copiedInviteId={copiedInviteId}
                onEditNameChange={setEditName}
                onStartEditing={() => {
                  setEditingHousehold(household.id)
                  setEditName(household.name)
                }}
                onCancelEditing={() => {
                  setEditingHousehold(null)
                  setEditName('')
                }}
                onSaveEditing={() => handleUpdateHousehold(household.id)}
                onSelectHousehold={() => setCurrentHousehold(household)}
                onToggleInviteForm={() => setShowInviteForm(!showInviteForm)}
                onInviteEmailChange={setInviteEmail}
                onInviteRoleChange={setInviteRole}
                onCreateInvite={handleCreateInvite}
                onCopyInviteLink={handleCopyInviteLink}
                onDeleteInvite={deleteInvite}
                onRemoveMember={(memberId) => removeMember(household.id, memberId)}
                onDeleteHousehold={() => handleDeleteHousehold(household.id, household.name)}
                onLeaveHousehold={() => leaveHousehold(household.id)}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {households.length === 0 && !showCreateForm && (
        <Card>
          <CardContent className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nog geen huishoudens</h3>
            <p className="text-gray-600 mb-6">
              Maak een huishouden om recepten te delen met familie of huisgenoten.
            </p>
            <Button onClick={() => setShowCreateForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Eerste Huishouden Maken
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
